import type { FeatureCollection } from 'geojson';
import type { Coordinate } from 'ol/coordinate';
import type Feature from 'ol/Feature.js';
import type { Geometry } from 'ol/geom';
import type { WatchSource } from 'vue';
import {
  computed,
  ref,
  shallowRef,
  watch,
} from 'vue';

import { DrawToolType } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';

import type { BaseDrawTool } from '#nora-ui/map/drawtools/base-draw-tool';
import { CircleTool } from '#nora-ui/map/drawtools/circle-tool';
import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import { FreehandLineTool } from '#nora-ui/map/drawtools/freehand-line-tool';
import { PolygonTool } from '#nora-ui/map/drawtools/polygon-tool';
import { RouteTool } from '#nora-ui/map/drawtools/route-tool';
import { RulerTool } from '#nora-ui/map/drawtools/ruler-tool';
import { SpotMapTool } from '#nora-ui/map/drawtools/spot-map-tool';
import { StraightLineTool } from '#nora-ui/map/drawtools/straight-line-tool';
import { SymbolTool } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool';
import type { SymbolOptions } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool.types';
import { SymbolCompletionBehavior } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool.types';
import { TacticalAnnotationTool } from '#nora-ui/map/drawtools/tactical-annotation/tactical-annotation-tool';
import { TextTool } from '#nora-ui/map/drawtools/text-tool';
import { MapUtils } from '#nora-ui/map/map/MapUtils';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import type { DrawToolUISnapshot, Persistable } from '#nora-ui/map/types/persistence.types';
import { colorWithOpacity } from '#nora-ui/utils/geojson-utils/geojson-styles';

export enum FillStyle {
  NoFill = 'no-fill',
  Solid = 'solid',
}

export enum LineStyle {
  NoLine = 'no-line',
  Solid = 'solid',
}

export enum LineWidth {
  UltraLight = 1.0,
  Light = 2.5,
  Normal = 5,
  Medium = 7.5,
  Bold = 10.0,
}

export enum DrawToolColor {
  Black = '#000000',
  White = '#ffffff',
  Green = '#52B57B',
  Orange = '#F2A53A',
  Purple = '#B22497',
  Red = '#E65451',
  Blue = '#88D2FB',
}

export const defaultFillColor = DrawToolColor.Black;
export const defaultFontStyle = '22px Helvetica,sans-serif';
export const defaultLineColor = DrawToolColor.Black;
export const defaultLineWidth = LineWidth.Normal;

export function isDrawToolColor(value: string): value is DrawToolColor {
  return Object.values(DrawToolColor).includes(value as DrawToolColor);
}

export function isFillStyle(value: string): value is FillStyle {
  return Object.values(FillStyle).includes(value as FillStyle);
}

export function isLineStyle(value: string): value is LineStyle {
  return Object.values(LineStyle).includes(value as LineStyle);
}

export function isLineWidth(value: number): value is LineWidth {
  return Object.values(LineWidth).includes(value);
}

/**
 * DrawToolUIManager handles UI state and tool creation for the drawing system.
 * It manages style options, active tools, and provides methods to create and switch between tools.
 */
export class DrawToolUIManager implements Persistable<DrawToolUISnapshot> {
  fillStyle = ref<FillStyle>(FillStyle.Solid);
  fillColor = ref<string>(defaultFillColor);
  fillOpacity = ref(0.75);
  lineStyle = ref<LineStyle>(LineStyle.Solid);
  lineWidth = ref(defaultLineWidth);
  lineColor = ref<string>(defaultLineColor);
  lineOpacity = ref(1.0);

  activeDrawTool = shallowRef<BaseDrawTool | null>(null);
  activeDrawToolType = ref<DrawToolType | null>(null);
  private editFeatureSnapshot: Feature<Geometry> | null = null;
  private isChangingTool = ref(false);
  isDrawingEnabled = ref(false);
  isEditing = ref(false);
  isToolListOpen = ref(false);

  drawStyleOptions = computed(() => ({
    fillColor:
      this.fillStyle.value === FillStyle.Solid
        ? colorWithOpacity(this.fillColor.value, this.fillOpacity.value)
        : colorWithOpacity(this.fillColor.value, 0.001),
    lineColor:
      this.lineStyle.value === LineStyle.Solid
        ? colorWithOpacity(this.lineColor.value, this.lineOpacity.value)
        : colorWithOpacity(this.lineColor.value, 0.001),
    lineWidth: this.lineWidth.value,
  }));

  constructor(private readonly map: NoraMap) {
    this.setupWatchers();
    this.bootstrap();
  }

  private setupWatchers(): void {
    watch(this.activeDrawTool, (newTool) => {
      this.map.drawToolManager.setDrawTool(newTool);
    });

    watch(this.drawStyleOptions, (newOptions) => {
      this.map.drawToolManager.updateDrawStyleOptions(newOptions);
    });
  }

  bootstrap(): void {
    this.map.drawToolManager.updateDrawStyleOptions(this.drawStyleOptions.value);
  }

  populateSource(featureCollection: FeatureCollection): void {
    MapUtils.replaceVectorSourceGeoJson(
      this.map.drawToolManager.vectorSource,
      featureCollection,
    );
  }

  enableDrawing(): void {
    const olMap = this.map.getOlMapOrThrow();

    const { vectorLayer } = this.map.drawToolManager;

    olMap.removeLayer(vectorLayer);
    olMap.addLayer(vectorLayer);
  }

  disableDrawing(): void {
    this.map.drawToolManager.deactivate();
  }

  undo(): void {
    this.map.drawToolManager.undo();
  }

  redo(): void {
    this.map.drawToolManager.redo();
  }

  editFeature(feature: Feature<Geometry>): void {
    const featureType = feature.get('drawToolType') as DrawToolType;
    if (!featureType) {
      return;
    }

    const editFeatureSnapshot = this.cloneFeature(feature);

    try {
      const isLoaded = this.loadEditFeature(
        featureType,
        this.cloneFeature(editFeatureSnapshot),
      );
      if (!isLoaded) {
        this.setDrawToolByType(null);
        return;
      }

      this.editFeatureSnapshot = editFeatureSnapshot;
      const featureId = feature.getId();
      MapUtils.removeFeatureFromSource(
        typeof featureId === 'string' ? featureId : feature,
        this.map.drawToolManager.vectorSource,
      );
      this.isEditing.value = true;
    }
    catch (error) {
      this.setDrawToolByType(null);
      console.error('Edit feature error', error);
    }
  }

  revertEdit(): void {
    if (!this.isEditing.value || !this.editFeatureSnapshot) {
      return;
    }

    const restoredFeature = this.cloneFeature(this.editFeatureSnapshot);
    this.setDrawToolByType(null);

    const drawLayerSource = this.map.drawToolManager.vectorSource;
    const restoredFeatureId = restoredFeature.getId();
    if (restoredFeatureId !== undefined) {
      const renderedFeature = drawLayerSource.getFeatureById(restoredFeatureId);
      if (renderedFeature) {
        drawLayerSource.removeFeature(renderedFeature);
      }
    }
    drawLayerSource.addFeature(restoredFeature);
    this.map.selectDrawFeature(restoredFeature);
  }

  private cloneFeature(feature: Feature<Geometry>): Feature<Geometry> {
    const clonedFeature = feature.clone();
    clonedFeature.setId(feature.getId());
    return clonedFeature;
  }

  private loadEditFeature(featureType: DrawToolType, feature: Feature<Geometry>): boolean {
    const drawTool = this.setDrawToolByType(featureType);

    if (!(drawTool instanceof CreateFeatureBaseDrawTool)) {
      return false;
    }

    const sourceDrawLayerId = feature.get('drawLayerId') as string | undefined;

    drawTool.loadFeature(feature);
    const editableFeature = drawTool.getFeature();
    if (!editableFeature) {
      return false;
    }

    if (isNonEmptyString(sourceDrawLayerId)) {
      drawTool.sourceDrawLayerId = sourceDrawLayerId;
      editableFeature.set('drawLayerId', sourceDrawLayerId);
    }

    if (drawTool instanceof SymbolTool) {
      drawTool.setCompletionBehavior(SymbolCompletionBehavior.Close);
    }
    drawTool.resetDirty();
    this.map.selectDrawFeature(editableFeature);
    return true;
  }

  getActiveDrawTool(): BaseDrawTool | null {
    return this.activeDrawTool.value;
  }

  // Expose the id of the feature which is being edited
  getEditingFeatureId(): string | undefined {
    if (!this.isEditing.value) {
      return undefined;
    }

    const featureId = this.editFeatureSnapshot?.getId() as string;
    return featureId ?? undefined;
  }

  createDrawToolFromType(
    drawToolType: DrawToolType | null,
    options?: object,
  ): BaseDrawTool | null {
    const drawToolManager = this.map.drawToolManager;

    switch (drawToolType) {
      case DrawToolType.line:
        return new StraightLineTool(drawToolManager);
      case DrawToolType.circle:
        return new CircleTool(drawToolManager);
      case DrawToolType.freehand:
        return new FreehandLineTool(drawToolManager);
      case DrawToolType.polygon:
        return new PolygonTool(drawToolManager);
      case DrawToolType.symbol:
        return new SymbolTool(drawToolManager, options);
      case DrawToolType.text:
        return new TextTool(drawToolManager);
      case DrawToolType.ruler:
        return new RulerTool(drawToolManager);
      case DrawToolType.route:
        return new RouteTool(drawToolManager);
      case DrawToolType.tactical_annotation:
        return new TacticalAnnotationTool(drawToolManager);
      case DrawToolType.spot:
        return new SpotMapTool(drawToolManager);
      case null:
      case DrawToolType.select:
      case DrawToolType.rectangle:
      case DrawToolType.building_annotation:
      case DrawToolType.erase:
      case DrawToolType.move:
      case DrawToolType.unstyled:
        return null;
    }
  }

  setDrawToolByType(
    drawToolType: DrawToolType | null,
    options?: object,
  ): BaseDrawTool | null {
    if (this.isChangingTool.value) {
      // Early return to avoid infinite recursion since some tools have different deactivate logic.
      return this.activeDrawTool.value;
    }

    this.isChangingTool.value = true;

    try {
      if (this.activeDrawTool.value) {
        this.activeDrawTool.value.deactivate();
      }

      this.isEditing.value = false;
      this.editFeatureSnapshot = null;

      const selectedTool = this.createDrawToolFromType(drawToolType, options);
      this.activeDrawTool.value = selectedTool;
      this.activeDrawToolType.value = drawToolType;

      return selectedTool;
    }
    finally {
      this.isChangingTool.value = false;
    }
  }

  unselectDrawTool(): void {
    this.setDrawToolByType(null);
  }

  /**
   * Places and persists a selected symbol at an OpenLayers map coordinate.
   *
   * @throws {TypeError} When the symbol drawing tool cannot be created.
   * @throws {Error} When no writable draw layer is available or persistence fails.
   */
  async placeSymbolAt(
    coordinate: Coordinate,
    symbol: SymbolOptions,
  ): Promise<void> {
    this.closeToolList();
    const drawTool = this.setDrawToolByType(DrawToolType.symbol, {
      ...symbol,
      completionBehavior: SymbolCompletionBehavior.Close,
    });

    if (!(drawTool instanceof SymbolTool)) {
      throw new TypeError('Could not create a symbol drawing tool.');
    }

    await drawTool.placeAt(coordinate);
  }

  openToolList(): void {
    this.unselectDrawTool();
    this.isToolListOpen.value = true;
  }

  closeToolList(): void {
    this.isToolListOpen.value = false;
  }

  /**
   * Serialize the draw tool UI preferences to a plain object for persistence.
   * Note: Only persists style preferences, not transient state like active tool.
   */
  serialize(): DrawToolUISnapshot {
    return {
      fillStyle: this.fillStyle.value,
      fillColor: this.fillColor.value,
      fillOpacity: this.fillOpacity.value,
      lineStyle: this.lineStyle.value,
      lineColor: this.lineColor.value,
      lineOpacity: this.lineOpacity.value,
      lineWidth: this.lineWidth.value,
    };
  }

  /**
   * Restore draw tool UI preferences from a persisted snapshot.
   */
  deserialize(snapshot: DrawToolUISnapshot): void {
    this.fillStyle.value = snapshot.fillStyle;
    this.fillColor.value = snapshot.fillColor;
    this.fillOpacity.value = snapshot.fillOpacity;
    this.lineStyle.value = snapshot.lineStyle;
    this.lineColor.value = snapshot.lineColor;
    this.lineOpacity.value = snapshot.lineOpacity;
    this.lineWidth.value = snapshot.lineWidth;
  }

  /**
   * Returns a watch source for triggering persistence when style preferences change.
   */
  getWatchSource(): WatchSource {
    return () => [
      this.fillStyle.value,
      this.fillColor.value,
      this.fillOpacity.value,
      this.lineStyle.value,
      this.lineColor.value,
      this.lineOpacity.value,
      this.lineWidth.value,
    ];
  }
}
